import React, { useEffect, useMemo } from 'react';

import { Copy } from '@carbon/icons-react';
import { GridOptions } from 'ag-grid-community';

import GridV2 from 'app/components/AdvancedGrid/GridV2';

import { useData } from 'app/contexts/dataProvider';
import { useScope } from 'app/contexts/scopeProvider';

import { IMPORT_ERRORS_BLOCK_SIZE } from 'app/global/variables';

import { GetFileImportErrors, GetFileImportErrorsVariables } from 'app/graphql/generated/graphqlApolloTypes';
import { useGetFileImportErrorsLazy } from 'app/graphql/queries/getFileImportErrors';

import useShowToast from 'app/hooks/useShowToast';

import { DataPanelViews, FileStatus } from 'app/models';

import block from 'utils/bem-css-modules';
import { formatMessage } from 'utils/messages/utils';

import style from './ImportErrorsPanel.module.pcss';
import buildImportErrorsPanelColumnDef from './ImportErrorsPanelColumnDef';

const b = block(style);

const ImportErrorsPanel: React.FC = () => {
  const { selectedPlanningCycle } = useScope();
  const { setSelectedDataView, setSelectedTable } = useData();
  const showToast = useShowToast();

  const [getFileImportErrorsList, { fetchMore }] = useGetFileImportErrorsLazy({
    fetchPolicy: 'network-only'
  });

  useEffect(() => {
    getFileImportErrorsList({
      variables: {
        planningCycleId: selectedPlanningCycle?.id,
        tableInput: {
          uploadFilter: {
            statuses: [FileStatus.FAILED]
          },
          startRow: 1,
          endRow: IMPORT_ERRORS_BLOCK_SIZE
        }
      }
    });
  }, [selectedPlanningCycle]);

  const onViewItemClicked = (data) => {
    setSelectedTable(data);
    setSelectedDataView(DataPanelViews.ERROR_DETAIL);
  };

  const getCustomItem: () => Array<{ text: string; icon: React.FunctionComponent; onClick: (data) => void }> = () => {
    return [
      {
        text: formatMessage('COPY_TO_CLIPBOARD'),
        icon: Copy,
        onClick: (data) => {
          const arrayOfErrors: Record<string, string>[] = Object.values(JSON.parse(data.messages ?? '{}'));
          const fileName = `${formatMessage('IMPORT_ERROR_MESSAGE', { tableName: data.tableName })}\n${formatMessage('ERRORS', { numOfErrors: arrayOfErrors.length })}.\n`;

          const copySummary = fileName + arrayOfErrors.map((error) => error.message).join('\n');
          navigator.clipboard.writeText(copySummary);
          showToast(formatMessage('ERROR_SUMMARY_COPIED_TO_CLIPBOARD'), 'success');
        }
      }
    ];
  };

  const importErrorPanelGridOptions = useMemo(
    (): GridOptions => ({
      pagination: true,
      paginationAutoPageSize: true,
      cacheBlockSize: IMPORT_ERRORS_BLOCK_SIZE,
      cacheOverflowSize: 2,
      maxConcurrentDatasourceRequests: 2,
      maxBlocksInCache: 2,
      columnDefs: buildImportErrorsPanelColumnDef({ onViewItemClicked, customItems: getCustomItem() }),
      rowModelType: 'serverSide',
      rowSelection: {
        mode: 'singleRow',
        checkboxes: false,
        enableClickSelection: true
      },
      onCellClicked: (event) => {
        if (!event.data || event.colDef.field === 'menu') {
          return;
        }

        setSelectedDataView(DataPanelViews.ERROR_DETAIL);
        setSelectedTable(event.data);
      },
      serverSideDatasource: {
        getRows: async (params) => {
          if (!params) return null;
          try {
            const { startRow, endRow } = params.request;

            const fetchMoreFiles = await fetchMore<GetFileImportErrors, GetFileImportErrorsVariables>({
              variables: {
                planningCycleId: selectedPlanningCycle?.id,
                tableInput: {
                  uploadFilter: {
                    statuses: [FileStatus.FAILED]
                  },
                  startRow: startRow + 1,
                  endRow
                }
              }
            });

            params.success({
              rowData: fetchMoreFiles.data.getPlanningCycleSpec.tables.tableList,
              rowCount: fetchMoreFiles.data.getPlanningCycleSpec.tables.totalNumOfTables
            });
          } catch {
            params.success({
              rowData: [],
              rowCount: 0
            });
          }
        }
      }
    }),
    [selectedPlanningCycle]
  );

  return (
    <div className={b('')}>
      <GridV2 {...importErrorPanelGridOptions} data-testid="import-errors-panel" />
    </div>
  );
};

export default ImportErrorsPanel;
